import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ChevronDown,
  LayoutDashboard,
  MessageCircle,
  Settings,
  UserRound,
  LogOut,
  Mail,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import UserAvatar, { userDisplayName } from './UserAvatar';
import { appUrl, talkUrl, wwwUrl, goProduct } from '@/config/hosts';
import './brand.css';

const MENU_STYLE = {
  width: 300,
  minWidth: 300,
  maxWidth: 'min(300px, calc(100vw - 24px))',
  overflowX: 'visible',
  overflowY: 'auto',
  padding: 8,
  zIndex: 120,
};

/**
 * Shared account menu, Platform, SiteNav (homepage), and marketing chrome.
 */
export default function UserAccountMenu({
  align = 'end',
  className = '',
  compact = false,
}) {
  const { user, isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();

  if (!isAuthenticated || !user) return null;

  const name = userDisplayName(user);
  const email = user.email || '';
  const isCompact = compact || className.includes('byo-account-trigger--compact');

  const go = (pathOrUrl) => () => goProduct(pathOrUrl, navigate);

  const handleLogout = async () => {
    await logout();
    goProduct(wwwUrl('/'), navigate);
  };

  const triggerClass = [
    'byo-account-trigger',
    isCompact ? 'byo-account-trigger--compact' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className={triggerClass}
          aria-label={`Account menu for ${name}`}
        >
          <UserAvatar user={user} size="md" />
          {!isCompact ? (
            <span className="byo-account-copy">
              <b>{name}</b>
              {email ? <small>{email}</small> : null}
            </span>
          ) : null}
          <ChevronDown className="byo-account-chevron" aria-hidden="true" />
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        align={align}
        side="bottom"
        sideOffset={10}
        alignOffset={0}
        collisionPadding={16}
        className="byo-account-menu"
        style={MENU_STYLE}
      >
        <div className="byo-account-menu-head">
          <UserAvatar user={user} size="lg" />
          <div className="byo-account-menu-identity">
            <p className="byo-account-menu-name">{name}</p>
            {email ? (
              <p className="byo-account-menu-email">
                <Mail className="byo-account-mail-icon" aria-hidden="true" />
                <a href={`mailto:${email}`}>{email}</a>
              </p>
            ) : null}
          </div>
        </div>

        <DropdownMenuSeparator className="byo-account-sep" />

        <DropdownMenuLabel className="byo-account-label">Navigate</DropdownMenuLabel>
        <DropdownMenuItem className="byo-account-item" onSelect={go(appUrl('/'))}>
          <LayoutDashboard aria-hidden="true" />
          <span>Dashboard</span>
        </DropdownMenuItem>
        <DropdownMenuItem className="byo-account-item" onSelect={go(talkUrl('/'))}>
          <MessageCircle aria-hidden="true" />
          <span>Talk to Vani</span>
        </DropdownMenuItem>

        <DropdownMenuSeparator className="byo-account-sep" />

        <DropdownMenuLabel className="byo-account-label">Your account</DropdownMenuLabel>
        <DropdownMenuItem className="byo-account-item" onSelect={go(appUrl('/account'))}>
          <Settings aria-hidden="true" />
          <span>Account & billing</span>
        </DropdownMenuItem>
        <DropdownMenuItem className="byo-account-item" onSelect={go(wwwUrl('/profile'))}>
          <UserRound aria-hidden="true" />
          <span>Profile</span>
        </DropdownMenuItem>

        <DropdownMenuSeparator className="byo-account-sep" />

        <DropdownMenuItem
          className="byo-account-item byo-account-item--danger"
          onSelect={handleLogout}
        >
          <LogOut aria-hidden="true" />
          <span>Log out</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
